import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef } from "react"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { HOME } from "@/shared/constants/backend-api"
import { computeReliefStatus, computeRebuildingStatus } from "../lib/relief-rebuilding-status"
import { useActiveWorkScopes } from "./use-active-work-scopes"

export interface FilterSearchHome {
  id: number
  address: string
  city: string | null
  county?: string | null
  latitude: number | null
  longitude: number | null
  isReported: boolean
  createdAt?: string | null
  updatedAt?: string | null
  homeowner: {
    id: number
    user: {
      id: number
      fullname: string
      phone: string
    }
    medicalNeeds: string | null
    homeownerAge: string | null
    isSingleParent: string | null
    numChildrenUnder18: number | null
    isFirstResponder: string | null
    isVeteran: string | null
    residentType: string | null
  }
  homeStatusInfo: {
    id: number
    overallStatus: string | null
  }
  /** Computed goodwill status (Not Started, In Progress, Unknown, Complete, No Assistance Required). */
  goodwillStatus?: string
  /** Derived relief status; added by `useFilterSearchHomes` using the Home Details calculation. */
  reliefStatus?: string
  /** Derived rebuilding status; added by `useFilterSearchHomes` using the Home Details calculation. */
  rebuildingStatus?: string
  roofingInfo: {
    id: number
    roofing: string | null
    roofTarpNeeded?: string | null
    kindOfRoofWork: string | null
  }
  repairabilityInfo: {
    id: number
    assessmentCompleted: string | null
    structureType: string | null
    residenceType: string | null
  }
  treeDebrisInfo?: {
    id: number
    treeWorkNeeded: string | null
    exteriorDebrisRemovalNeeded: string | null
    heavyEquipmentNeeded: string | null
  }
  muckGutInfo?: {
    id: number
    muckGutNeeded: string | null
  }
  moldRemediationInfo?: {
    id: number
    moldRemediation: string | null
  }
  rebuildPrepInfo?: {
    id: number
    rebuildPrep: string | null
  }
  roughCarpentryInfo?: {
    id: number
    roughCarpentry: string | null
  }
  electricalInfo?: {
    id: number
    electrical: string | null
    electricalRoughIn: string | null
  }
  hvacInfo?: {
    id: number
    hvac: string | null
    mechanicalRoughIn: string | null
  }
  plumbingInfo?: {
    id: number
    plumbing: string | null
    plumbingRoughIn: string | null
  }
  bathroomRebuildInfo?: {
    id: number
    bathroomRebuild: string | null
  }
  crawlSpaceInfo?: {
    id: number
    crawlSpaceMuck: string | null
    crawlSpaceInsulation: string | null
  }
  insulationInstallInfo?: {
    id: number
    insulationInstall: string | null
  }
  drywallHangingInfo?: {
    id: number
    drywallHanging: string | null
  }
  mudAndTapeInfo?: {
    id: number
    mudAndTape: string | null
  }
  cabinetInstallInfo?: {
    id: number
    cabinetInstall: string | null
    counterInstall: string | null
  }
  interiorPaintInfo?: {
    id: number
    interiorPaint: string | null
  }
  installInteriorDoorsInfo?: {
    id: number
    installInteriorDoors: string | null
  }
  trimInstallInfo?: {
    id: number
    trimInstall: string | null
  }
  flooringInstallInfo?: {
    id: number
    flooringInstall: string | null
  }
  exteriorDoorInstallInfo?: {
    id: number
    exteriorDoorInstall: string | null
  }
  miscellaneousInfo?: {
    id: number
    miscellaneous: string | null
    shutterInstall: string | null
    drivewayRepair: string | null
  }
}

export interface FilterSearchHomesResponse {
  success: boolean
  data: FilterSearchHome[]
  total?: number
  message?: string
  error?: string
}

/**
 * Hook to fetch home data for filter-search page
 * Returns essential fields plus filter fields: overallStatus, roofing, medicalNeeds, etc.
 * 
 * @param regionId - Optional region ID to filter homes by region
 * @param workscope - Optional comma-separated work scope slugs; when set, backend returns only homes that have at least one of these work scopes "in scope"
 * @returns {Object} {
 *  data: FilterSearchHomesResponse,
 *  isLoading: boolean,
 *  error: Error | null
 * }
 */
type UseFilterSearchHomesOptions = {
  enabled?: boolean
  /** When set, fetch can start immediately without waiting for regionId (backend resolves slug to id). */
  regionSlug?: string
  workscope?: string
  workscopeStatus?: string
  workscopeGoodwill?: string
  workscopeGoodwillStatus?: string
  /** When set, first request fetches only this many rows (faster initial load). Rest are fetched in background and merged. */
  initialLimit?: number
}

export function useFilterSearchHomes(regionId?: number, includeInactive: boolean = true, options: UseFilterSearchHomesOptions = {}) {
  const { enabled = true, regionSlug, workscope, workscopeStatus, workscopeGoodwill, workscopeGoodwillStatus, initialLimit } = options
  const queryClient = useQueryClient()
  const { activeWorkScopes } = useActiveWorkScopes()
  const canFetch = Boolean((regionId != null || (regionSlug?.trim() ?? '')) && enabled)
  const queryKey = ['homes', 'filter-search', regionId ?? regionSlug ?? '', includeInactive, workscope ?? '', workscopeStatus ?? '', workscopeGoodwill ?? '', workscopeGoodwillStatus ?? '', initialLimit ?? '']
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<FilterSearchHomesResponse> => {
      const params = new URLSearchParams()
      if (regionId != null) {
        params.set('regionId', regionId.toString())
      } else if (regionSlug?.trim()) {
        params.set('regionSlug', regionSlug.trim())
      }
      if (includeInactive) {
        params.set('includeInactive', 'true')
      }
      if (initialLimit != null && initialLimit > 0) {
        params.set('limit', initialLimit.toString())
        params.set('offset', '0')
      }
      if (workscope) {
        const slug = workscope.split(',').map((s) => s.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')).filter(Boolean).join(',')
        if (slug) params.set('workscope', slug)
      }
      if (workscopeStatus) {
        params.set('workscopeStatus', workscopeStatus)
      }
      if (workscopeGoodwill) {
        params.set('workscopeGoodwill', workscopeGoodwill)
      }
      if (workscopeGoodwillStatus) {
        params.set('workscopeGoodwillStatus', workscopeGoodwillStatus)
      }

      const url = `${HOME.base}/filter-search${params.toString() ? `?${params.toString()}` : ''}`
      const res = await authFetchJson<any>(url)
      if (!res.success) {
        throw new Error(res.message || 'Failed to fetch filter search homes')
      }

      const data = res.data?.data ?? res.data
      const list = Array.isArray(data) ? data : []
      const total = typeof res.data?.total === 'number' ? res.data.total : (typeof (res as any).total === 'number' ? (res as any).total : undefined)
      return { success: true, data: list, total } as FilterSearchHomesResponse
    },
    staleTime: workscope || workscopeStatus || workscopeGoodwill || workscopeGoodwillStatus ? 0 : 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: workscope || workscopeStatus || workscopeGoodwill || workscopeGoodwillStatus ? 'always' : false,
    enabled: canFetch,
  })

  const restFetchStarted = useRef(false)
  useEffect(() => {
    const d = query.data
    if (
      !query.isSuccess ||
      !d?.success ||
      !Array.isArray(d.data) ||
      typeof d.total !== 'number' ||
      d.total <= d.data.length ||
      restFetchStarted.current ||
      initialLimit == null ||
      initialLimit <= 0
    ) {
      return
    }
    restFetchStarted.current = true
    const restOffset = d.data.length
    const restLimit = d.total - restOffset
    const params = new URLSearchParams()
    if (regionId != null) params.set('regionId', regionId.toString())
    else if (regionSlug?.trim()) params.set('regionSlug', regionSlug.trim())
    if (includeInactive) params.set('includeInactive', 'true')
    params.set('limit', String(restLimit))
    params.set('offset', String(restOffset))
    if (workscope) {
      const slug = workscope.split(',').map((s) => s.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')).filter(Boolean).join(',')
      if (slug) params.set('workscope', slug)
    }
    if (workscopeStatus) params.set('workscopeStatus', workscopeStatus)
    if (workscopeGoodwill) params.set('workscopeGoodwill', workscopeGoodwill)
    if (workscopeGoodwillStatus) params.set('workscopeGoodwillStatus', workscopeGoodwillStatus)
    const url = `${HOME.base}/filter-search?${params.toString()}`
    const mergeKey = ['homes', 'filter-search', regionId ?? regionSlug ?? '', includeInactive, workscope ?? '', workscopeStatus ?? '', workscopeGoodwill ?? '', workscopeGoodwillStatus ?? '', initialLimit ?? '']
    authFetchJson<any>(url)
      .then((res) => {
        if (!res.success) return null
        const data = res.data?.data ?? res.data
        const list = Array.isArray(data) ? data : []
        return { success: true, data: list } as FilterSearchHomesResponse
      })
      .then((json: FilterSearchHomesResponse | null) => {
        if (!json || !Array.isArray(json.data)) return
        const current = queryClient.getQueryData<FilterSearchHomesResponse>(mergeKey)
        if (current?.success && Array.isArray(current.data)) {
          queryClient.setQueryData<FilterSearchHomesResponse>(mergeKey, {
            ...current,
            data: [...current.data, ...json.data],
          })
        }
      })
      .finally(() => {
        restFetchStarted.current = false
      })
  }, [query.isSuccess, query.data, initialLimit, queryClient, regionId, regionSlug, includeInactive, workscope, workscopeStatus, workscopeGoodwill, workscopeGoodwillStatus])

  /**
   * Derive Relief / Rebuilding status here so the list, Reports, and Home Details
   * all use the same calculation (`lib/relief-rebuilding-status`).
   */
  const data = useMemo(() => {
    const response = query.data
    if (!response?.success || !Array.isArray(response.data)) return response
    return {
      ...response,
      data: response.data.map((home) => ({
        ...home,
        reliefStatus: computeReliefStatus({ home }, activeWorkScopes).displayLabel,
        rebuildingStatus: computeRebuildingStatus({ home }, activeWorkScopes).displayLabel,
      })),
    }
  }, [query.data, activeWorkScopes])

  return { ...query, data }
}
